// src/app/modules/Devices/components/FingerprintsTab.jsx
import React, { useState } from 'react';
import { Fingerprint, Trash2, Unlink, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { devicesApi, errorMessage } from '../api';
import { usePolling, useEvent, shortDate } from '../utils';
import { Pill, EmptyState, Button, DvModal, Spinner } from './ui';

const REFRESH_MS = 5000;

export function FingerprintsTab({ orgId, device, canManage, onEnrol, onChanged }) {
  const [rows, setRows] = useState(null);
  const [confirm, setConfirm] = useState(null); // { enrollment, unlink }
  const [busy, setBusy] = useState(false);

  const load = useEvent(async () => {
    try {
      const res = await devicesApi.enrollments(orgId, device.device_id, { status: 'live', limit: 200 });
      setRows(res.enrollments.sort((a, b) => a.uid - b.uid));
    } catch (err) {
      if (rows === null) setRows([]);
    }
  });
  usePolling(load, REFRESH_MS);

  const remove = async () => {
    const { enrollment, unlink } = confirm;
    setBusy(true);
    try {
      await devicesApi.removeEnrollment(orgId, device.device_id, enrollment.enrollment_id, unlink);
      toast.success(unlink ? 'Fingerprint unlinked' : 'Deletion queued — it runs the next time someone presses the device button');
      setConfirm(null);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (rows === null) return <div className="dv-card__body" style={{ textAlign: 'center' }}><Spinner /></div>;

  return (
    <>
      {rows.length === 0 ? (
        <EmptyState
          icon={Fingerprint}
          title="No fingerprints on this device"
          action={canManage && <Button variant="primary" icon={Fingerprint} onClick={onEnrol}>Enrol a fingerprint</Button>}
        >
          Enrol staff so they can clock in and out with a fingerprint.
        </EmptyState>
      ) : (
        <div className="dv-table-wrap">
          <table className="dv-table">
            <thead>
              <tr><th>Slot</th><th>Person</th><th>Status</th><th>Enrolled</th>{canManage && <th />}</tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.enrollment_id}>
                  <td className="dv-num">#{e.uid}</td>
                  <td>
                    <div className="dv-strong">{e.display_name || 'Unknown member'}</div>
                    {e.employee_id && <div className="dv-sub">{e.employee_id}</div>}
                  </td>
                  <td>
                    {e.status === 'active'
                      ? <Pill tone="success">Active</Pill>
                      : <Pill tone="warning" title="Waiting for the device to confirm the deletion">Deleting…</Pill>}
                    {e.source === 'manual_link' && <div className="dv-sub">Linked manually</div>}
                  </td>
                  <td>{shortDate(e.enrolled_at)}</td>
                  {canManage && (
                    <td className="dv-actions">
                      {e.status === 'active' && (
                        <>
                          <Button size="sm" variant="ghost" icon={Unlink} title="Unlink without touching the sensor" onClick={() => setConfirm({ enrollment: e, unlink: true })}>Unlink</Button>
                          <Button size="sm" variant="danger" icon={Trash2} onClick={() => setConfirm({ enrollment: e, unlink: false })}>Delete</Button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DvModal
        show={!!confirm}
        onHide={() => setConfirm(null)}
        title={confirm?.unlink ? 'Unlink fingerprint?' : 'Delete fingerprint?'}
        subtitle={confirm && `${confirm.enrollment.display_name || 'Unknown member'} · slot #${confirm.enrollment.uid}`}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="danger-solid" onClick={remove} loading={busy}>{confirm?.unlink ? 'Unlink' : 'Delete'}</Button>
          </>
        )}
      >
        {confirm?.unlink ? (
          <div className="dv-notice">
            <Info size={16} />
            <span>This only removes the link in Sety — the fingerprint stays on the sensor and will scan as <b>unknown</b>. Use it when the sensor was wiped or replaced. To remove someone properly, use <b>Delete</b>.</span>
          </div>
        ) : (
          <p style={{ margin: 0 }}>
            The fingerprint is removed from the sensor the next time someone presses the device button.
            Until then scans with this finger are recorded but don’t clock anyone in. Attendance history is kept.
          </p>
        )}
      </DvModal>
    </>
  );
}

export default FingerprintsTab;
